import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../../auth/AuthProvider';
import { queryKeys } from '../../../api/query-keys';
import type { RoleId } from '../types/user';
import {
  assignRoles,
  inviteUser,
  listUsers,
  reactivateUser,
  removeUser,
  resendInvitation,
  suspendUser,
} from './users.api';

export function useUsers() {
  const auth = useAuth();
  return useQuery({
    queryKey: queryKeys.users(auth.activeCustomerId),
    queryFn: () => listUsers(auth.activeCustomerId!),
    enabled: Boolean(auth.activeCustomerId && auth.can('user:read')),
  });
}

function useUserMutation<TInput, TResult>(mutationFn: (customerId: string, input: TInput) => Promise<TResult>) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => mutationFn(auth.activeCustomerId!, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users(auth.activeCustomerId) });
      await auth.refreshProfile();
    },
  });
}

export function useInviteUser() {
  return useUserMutation((customerId, input: { email: string; roleId: RoleId; facilityIds: string[] }) => inviteUser(customerId, input));
}

export function useAssignRoles() {
  return useUserMutation((customerId, input: { membershipId: string; roleIds: RoleId[] }) => assignRoles(customerId, input.membershipId, input.roleIds));
}

export function useResendInvitation() {
  return useUserMutation((customerId, membershipId: string) => resendInvitation(customerId, membershipId));
}

export function useSuspendUser() {
  return useUserMutation((customerId, membershipId: string) => suspendUser(customerId, membershipId));
}

export function useReactivateUser() {
  return useUserMutation((customerId, membershipId: string) => reactivateUser(customerId, membershipId));
}

export function useRemoveUser() {
  return useUserMutation((customerId, membershipId: string) => removeUser(customerId, membershipId));
}
